import * as React from 'react'

import Localization from '../../localization/LocalizationManager'
import { ListHeader } from '../ListHeader'
import { ListSelector } from '../ListSelector'
import { ResourceCard } from '../ResourceCardList/ResourceCard'
import { buildResourceCardModel } from '../ResourceCardList/resourceCardMapper'
import {
  ICardConfiguration,
  IResourceProperty,
  IResourceRecord,
} from '../ResourceCardList/types'
import { useResourceLinkContext } from './ResourceLinkContext'

interface IProps {
  title: string
  records: IResourceRecord[]
  resourceTypeId: string
  cardConfiguration: ICardConfiguration
  properties: IResourceProperty[]
  getDisplayValue: (propertyName: string, value: any) => string
  emptyMessage?: string
  children?: (record: IResourceRecord, model: any) => React.ReactNode
}

export const ResourceLinkPicker: React.FC<IProps> = ({
  title,
  records,
  resourceTypeId,
  cardConfiguration,
  properties,
  getDisplayValue,
  emptyMessage,
  children,
}) => {
  const {
    isPickerOpen,
    closePicker,
    availableTargets,
    linkTo,
    loading,
    error,
  } = useResourceLinkContext()

  const [searchQuery, setSearchQuery] = React.useState('')

  React.useEffect(() => {
    if (!isPickerOpen) {
      setSearchQuery('')
    }
  }, [isPickerOpen])

  if (!isPickerOpen) return null

  const availableIds = new Set(availableTargets.map(t => t.id))
  const query = searchQuery.toLowerCase()

  const filteredRecords = records.filter(r => {
    if (!availableIds.has(r.id)) return false
    if (!query) return true
    return (
      r.name.toLowerCase().includes(query) ||
      (r.infuseId && r.infuseId.toLowerCase().includes(query))
    )
  })

  return (
    <>
      <div
        className="tw:absolute tw:inset-0 tw:z-[99] tw:bg-black/40"
        onClick={closePicker}
      />
      <div className="tw:absolute tw:top-[10%] tw:left-0 tw:right-0 tw:bottom-0 tw:z-[100] tw:bg-background tw:rounded-t-xl tw:overflow-hidden tw:flex tw:flex-col">
        <ListSelector.Title title={title} onClose={closePicker} />
        <ListHeader.DisplayBar
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            autoFocus: true,
          }}
        />

        {error && (
          <div className="tw:text-body-md tw:text-destructive tw:bg-destructive/10 tw:px-3 tw:py-1 tw:border-0 tw:border-b tw:border-solid tw:border-input-soft">
            {error}
          </div>
        )}

        <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-0.5 tw:overflow-y-auto tw:p-2.5">
          {filteredRecords.length === 0 ? (
            <div className="tw:text-body-md tw:text-muted-foreground tw:text-center tw:p-3">
              {emptyMessage || Localization.translator.noResults}
            </div>
          ) : (
            filteredRecords.map(record => {
              const model = buildResourceCardModel({
                record,
                resourceTypeId,
                cardConfiguration,
                properties,
                getDisplayValue,
              })

              if (children) {
                return (
                  <div
                    key={record.id}
                    onClick={() => !loading && linkTo(record.id)}
                  >
                    {children(record, model)}
                  </div>
                )
              }

              return (
                <ResourceCard.Standard
                  key={record.id}
                  model={model}
                  variant="menu"
                  className="tw:transition-colors tw:hover:bg-accent-soft"
                  onClick={() => !loading && linkTo(record.id)}
                />
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
